import type {
	BundleRecommendationResponse,
	PlacementProductMeta,
	PlacementsAndPromosResponse,
	ProductBoxModel,
	RecommendationItem,
} from "../types/viewmodel";
import { productImageUrl } from "../utils/productUtils";

interface PlacementsAndPromosRequest {
	pageType: string;
	itemPageDataModel: { id: string };
	recsPlacementIds: string[];
	promoPlacementIds: string[];
	currentUrl: string;
	referrer: string | null;
}

const IMAGE_SIZE = 200;

const toRecommendationItem = (
	product: ProductBoxModel,
	meta?: PlacementProductMeta,
): RecommendationItem => {
	const { price } = product;
	const image = product.productImages?.[0];
	const showMonthlyPrice =
		price.isApplicableForLeasing && !price.hideFlexPrice && !!price.monthlyPrice;

	return {
		id: product.materialNumber,
		name: product.name,
		description: product.description,
		imageURL: image
			? `/${image.url}/${IMAGE_SIZE}/${image.fileName}`
			: productImageUrl(product.materialNumber),
		imageAltText: image?.altText || product.name,
		price: price.listPriceNumber,
		priceBefore: price.listPriceNumber + price.discountNumber,
		monthlyPriceFormatted: showMonthlyPrice ? price.monthlyPrice : undefined,
		bundleGroup: meta?.bundleGroup ?? undefined,
	};
};

export async function getRecommendations(
	bundleId: string,
): Promise<BundleRecommendationResponse> {
	const request: PlacementsAndPromosRequest = {
		pageType: "Item",
		itemPageDataModel: { id: bundleId },
		recsPlacementIds: ["kpc_accessories"],
		promoPlacementIds: [],
		currentUrl: window.location.href,
		referrer: document.referrer || null,
	};

	const response = await fetch(
		"/api-product/v1/recommendation/placements-and-promos",
		{
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request),
			credentials: "same-origin",
		},
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
	}

	const data: PlacementsAndPromosResponse = await response.json();

	return {
		placements: (data.placements ?? []).map((placement) => ({
			message: placement.message,
			placementName: placement.placementName,
			items: (placement.productBoxModels ?? []).map((product) =>
				toRecommendationItem(product, placement.meta?.[product.materialNumber]),
			),
		})),
	};
}
